<script>
import {
  DEFAULT_COLOR_PALETTE,
  normalizeHexColor,
  shadeHexColor,
} from '@/editor/colors'

export default {
  name: 'VybyColorPicker',
  props: {
    value: {
      type: String,
      required: false,
      default: '#ffffff',
    },
    fallback: {
      type: String,
      required: false,
      default: '#ffffff',
    },
    title: {
      type: String,
      required: false,
      default: 'Color',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    palette: {
      type: Array,
      required: false,
      default: () => DEFAULT_COLOR_PALETTE,
    },
    placement: {
      type: String,
      required: false,
      default: 'bottom-start',
    },
    live: {
      type: Boolean,
      required: false,
      default: false,
    },
    size: {
      type: Number,
      required: false,
      default: 24,
    },
    swatchSize: {
      type: Number,
      required: false,
      default: 14,
    },
    actionButtons: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  emits: ['input', 'change', 'open', 'close'],
  data() {
    return {
      open: false,
      draft: '',
      popoverX: 0,
      popoverY: 0,
      shadeValue: 0,
      shadeBase: '',
      shadeDragging: false,
    }
  },
  computed: {
    color() {
      return normalizeHexColor(this.value, this.fallback) || '#ffffff'
    },
    draftColor() {
      return normalizeHexColor(this.draft, this.color) || this.color
    },
    hexInputValue: {
      get() {
        return this.draft || this.color
      },
      set(value) {
        this.draft = `${value || ''}`.trim()
      },
    },
    normalizedPalette() {
      const colors = Array.isArray(this.palette) ? this.palette : DEFAULT_COLOR_PALETTE
      return Array.from(
        new Set(
          colors
            .map((color) => normalizeHexColor(color))
            .filter(Boolean),
        ),
      )
    },
    triggerStyle() {
      return {
        '--vyby-color-picker-size': `${Math.max(10, Number(this.size) || 24)}px`,
        '--vyby-color-picker-swatch-size': `${Math.max(8, Number(this.swatchSize) || 14)}px`,
        '--vyby-color-picker-color': this.color,
      }
    },
    draftStyle() {
      return {
        '--vyby-color-picker-color': this.draftColor,
      }
    },
    shadeBaseColor() {
      return normalizeHexColor(this.shadeBase, this.draftColor) || this.draftColor
    },
    shadePreviewColor() {
      return shadeHexColor(this.shadeBaseColor, this.shadeValue, this.draftColor)
    },
    shadeSliderStyle() {
      return {
        '--vyby-color-picker-color': this.shadeBaseColor,
        '--vyby-color-picker-shade-preview': this.shadePreviewColor,
        '--vyby-color-picker-shade-y': `${50 - this.shadeValue * 50}%`,
      }
    },
    popoverStyle() {
      return {
        left: `${this.popoverX}px`,
        top: `${this.popoverY}px`,
      }
    },
  },
  watch: {
    value() {
      if (!this.open) this.draft = this.color
    },
    open(next) {
      if (next) {
        this.draft = this.color
        this.shadeValue = 0
        this.shadeBase = this.color
        this.$nextTick(this.updatePopoverPosition)
      }
    },
  },
  mounted() {
    this.draft = this.color
    this.shadeBase = this.color
    document.addEventListener('mousedown', this.onDocumentMouseDown, true)
    window.addEventListener('resize', this.updatePopoverPosition, true)
    window.addEventListener('scroll', this.updatePopoverPosition, true)
  },
  beforeUnmount() {
    document.removeEventListener('mousedown', this.onDocumentMouseDown, true)
    window.removeEventListener('resize', this.updatePopoverPosition, true)
    window.removeEventListener('scroll', this.updatePopoverPosition, true)
    this.detachShadeDragListeners()
  },
  methods: {
    togglePicker(e) {
      if (e && typeof e.preventDefault === 'function') e.preventDefault()
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation()
      if (this.disabled) return
      if (this.open) {
        this.closePicker()
        return
      }
      this.open = true
      this.$emit('open')
    },
    closePicker() {
      if (!this.open) return
      this.open = false
      this.draft = this.color
      this.shadeDragging = false
      this.detachShadeDragListeners()
      this.resetShadeSlider(this.color)
      this.$emit('close')
    },
    updatePopoverPosition() {
      if (!this.open) return
      const trigger = this.$refs.trigger
      if (!trigger || typeof trigger.getBoundingClientRect !== 'function') return
      const rect = trigger.getBoundingClientRect()
      const pad = 10
      const popover = this.$refs.popover
      const width = Number(popover?.offsetWidth) || 250
      const height = Number(popover?.offsetHeight) || 224
      const place = `${this.placement || ''}`
      let x = place.includes('end') ? rect.right - width : rect.left
      let y = place.includes('top') ? rect.top - height - 8 : rect.bottom + 8

      x = Math.min(Math.max(x, pad), Math.max(pad, window.innerWidth - width - pad))
      y = Math.min(Math.max(y, pad), Math.max(pad, window.innerHeight - height - pad))
      this.popoverX = Math.round(x)
      this.popoverY = Math.round(y)
    },
    onDocumentMouseDown(e) {
      if (!this.open) return
      const target = e?.target
      if (this.$refs.trigger && this.$refs.trigger.contains(target)) return
      if (this.$refs.popover && this.$refs.popover.contains(target)) return
      this.closePicker()
    },
    onHexInput(e) {
      const value = `${e?.target?.value || ''}`.trim()
      this.hexInputValue = value
      const color = normalizeHexColor(value)
      if (!color) return
      this.setDraftColor(color)
    },
    selectPaletteColor(color) {
      const normalized = normalizeHexColor(color)
      if (!normalized) return
      this.setDraftColor(normalized)
    },
    setDraftColor(color, { resetShade = true } = {}) {
      const normalized = normalizeHexColor(color, this.color)
      if (!normalized) return
      this.draft = normalized
      if (resetShade) this.resetShadeSlider(normalized)
      this.$emit('input', normalized)
      if (this.live) this.$emit('change', normalized)
    },
    applyColor() {
      const color = normalizeHexColor(this.draft, this.color)
      if (!color) return
      this.draft = color
      this.$emit('input', color)
      this.$emit('change', color)
      this.open = false
      this.$emit('close')
    },
    resetShadeSlider(color = null) {
      const next = normalizeHexColor(color, this.draftColor) || this.draftColor
      this.shadeValue = 0
      this.shadeBase = next
    },
    shadeValueFromClientY(clientY) {
      const track = this.$refs.shadeTrack
      if (!track || typeof track.getBoundingClientRect !== 'function') return 0
      const rect = track.getBoundingClientRect()
      if (!rect.height) return 0
      const pct = Math.max(0, Math.min(1, (Number(clientY) - rect.top) / rect.height))
      return Math.max(-1, Math.min(1, 1 - pct * 2))
    },
    updateShadeFromEvent(e) {
      const y = typeof e?.clientY === 'number' ? e.clientY : 0
      this.shadeValue = this.shadeValueFromClientY(y)
    },
    onShadePointerDown(e) {
      if (e && typeof e.preventDefault === 'function') e.preventDefault()
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation()
      if (!this.shadeBase) this.shadeBase = this.draftColor
      this.shadeDragging = true
      this.updateShadeFromEvent(e)
      this.attachShadeDragListeners()
    },
    onShadePointerMove(e) {
      if (!this.shadeDragging) return
      this.updateShadeFromEvent(e)
    },
    onShadePointerUp(e) {
      if (!this.shadeDragging) return
      this.updateShadeFromEvent(e)
      this.shadeDragging = false
      this.detachShadeDragListeners()
      this.setDraftColor(this.shadePreviewColor, { resetShade: false })
    },
    attachShadeDragListeners() {
      this.detachShadeDragListeners()
      window.addEventListener('pointermove', this.onShadePointerMove, { passive: true })
      window.addEventListener('pointerup', this.onShadePointerUp, { passive: true })
      window.addEventListener('pointercancel', this.onShadePointerUp, { passive: true })
    },
    detachShadeDragListeners() {
      window.removeEventListener('pointermove', this.onShadePointerMove)
      window.removeEventListener('pointerup', this.onShadePointerUp)
      window.removeEventListener('pointercancel', this.onShadePointerUp)
    },
    onKeydown(e) {
      if (e?.key === 'Escape') {
        e.preventDefault()
        this.closePicker()
      }
      if (e?.key === 'Enter') {
        e.preventDefault()
        this.applyColor()
      }
    },
  },
}
</script>

<template>
  <span class="vyby-color-picker">
    <button
      ref="trigger"
      type="button"
      class="vyby-color-picker__trigger"
      :class="{ 'is-open': open, 'is-disabled': disabled }"
      :style="triggerStyle"
      :title="title"
      :aria-label="title"
      :disabled="disabled"
      @click="togglePicker"
    >
      <span class="vyby-color-picker__swatch" aria-hidden="true" />
    </button>

    <teleport to="body">
      <div
        v-if="open"
        ref="popover"
        class="vyby-color-picker__popover vyby-stroke-anchor vyby-stroke-radial"
        :style="popoverStyle"
        role="dialog"
        :aria-label="title"
        @mousedown.stop
        @keydown.esc.prevent.stop="closePicker"
      >
        <div class="vyby-color-picker__body">
          <div class="vyby-color-picker__main">
            <div class="vyby-color-picker__preview-row">
              <span
                class="vyby-color-picker__preview"
                :style="draftStyle"
                aria-hidden="true"
              />
              <input
                class="vyby-color-picker__hex"
                :value="hexInputValue"
                spellcheck="false"
                maxlength="7"
                @input="onHexInput"
                @keydown.stop="onKeydown"
              />
            </div>

            <div class="vyby-color-picker__palette" aria-label="Color presets">
              <button
                v-for="color in normalizedPalette"
                :key="color"
                type="button"
                class="vyby-color-picker__palette-btn"
                :class="{ 'is-selected': draftColor === color }"
                :style="{ '--vyby-color-picker-color': color }"
                :title="color"
                @click="selectPaletteColor(color)"
              >
                <span class="vyby-color-picker__palette-swatch" aria-hidden="true" />
              </button>
            </div>
          </div>

          <button
            ref="shadeTrack"
            type="button"
            class="vyby-color-picker__shade-slider"
            :class="{ 'is-dragging': shadeDragging }"
            :style="shadeSliderStyle"
            title="Adjust lightness"
            aria-label="Adjust lightness"
            @pointerdown="onShadePointerDown"
          >
            <span class="vyby-color-picker__shade-thumb" aria-hidden="true" />
          </button>
        </div>

        <div class="vyby-color-picker__actions">
          <slot name="extra" />
          <button
            v-if="actionButtons"
            type="button"
            class="vyby-color-picker__action"
            @click="closePicker"
          >
            Cancel
          </button>
          <button
            v-if="actionButtons"
            type="button"
            class="vyby-color-picker__action is-primary"
            @click="applyColor"
          >
            Apply
          </button>
        </div>
      </div>
    </teleport>
  </span>
</template>

<style scoped>
.vyby-color-picker {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.vyby-color-picker__trigger {
  position: relative;
  width: var(--vyby-color-picker-size);
  height: var(--vyby-color-picker-size);
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.vyby-color-picker__trigger:hover,
.vyby-color-picker__trigger.is-open {
  background: rgba(255, 255, 255, 0.06);
}

.vyby-color-picker__trigger.is-disabled {
  cursor: default;
  opacity: 0.55;
}

.vyby-color-picker__swatch,
.vyby-color-picker__preview,
.vyby-color-picker__palette-swatch {
  display: block;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.5);
  background:
    radial-gradient(circle at 32% 30%, rgba(255, 255, 255, 0.9) 0 18%, rgba(255, 255, 255, 0.18) 30%, rgba(255, 255, 255, 0) 52%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0) 48%),
    var(--vyby-color-picker-color);
  box-shadow:
    inset 0 1px 1px rgba(255, 255, 255, 0.28),
    0 0 0 1px rgba(0, 0, 0, 0.16),
    0 0 10px color-mix(in srgb, var(--vyby-color-picker-color) 45%, transparent);
}

.vyby-color-picker__swatch {
  width: var(--vyby-color-picker-swatch-size);
  height: var(--vyby-color-picker-swatch-size);
}

.vyby-color-picker__popover {
  position: fixed;
  z-index: 10050;
  width: 250px;
  padding: 10px;
  border-radius: 16px;
  border: 1.25px solid rgba(255, 255, 255, 0.32);
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.03)),
    rgba(13, 13, 13, 0.72);
  backdrop-filter: blur(18px);
  box-shadow: 0 18px 60px rgba(0, 0, 0, 0.55);
}

.vyby-color-picker__body {
  display: flex;
  align-items: stretch;
  gap: 10px;
}

.vyby-color-picker__main {
  min-width: 0;
  flex: 1;
}

.vyby-color-picker__preview-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.vyby-color-picker__preview {
  width: 28px;
  height: 28px;
  flex: 0 0 auto;
}

.vyby-color-picker__hex {
  min-width: 0;
  flex: 1;
  height: 30px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(0, 0, 0, 0.24);
  color: rgba(255, 255, 255, 0.92);
  font-size: 12px;
  padding: 0 10px;
  outline: none;
}

.vyby-color-picker__hex:focus {
  border-color: rgba(243, 112, 33, 0.58);
  box-shadow: 0 0 0 1px rgba(243, 112, 33, 0.2);
}

.vyby-color-picker__palette {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 6px;
}

.vyby-color-picker__palette-btn {
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.04);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.vyby-color-picker__palette-btn:hover,
.vyby-color-picker__palette-btn.is-selected {
  background: rgba(255, 255, 255, 0.1);
}

.vyby-color-picker__palette-btn.is-selected {
  box-shadow: inset 0 0 0 1px rgba(243, 112, 33, 0.42);
}

.vyby-color-picker__palette-swatch {
  width: 18px;
  height: 18px;
}

.vyby-color-picker__shade-slider {
  position: relative;
  width: 22px;
  min-height: 104px;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  background:
    linear-gradient(
      to top,
      #000000 0%,
      var(--vyby-color-picker-color) 50%,
      #ffffff 100%
    );
  box-shadow:
    inset 0 1px 5px rgba(0, 0, 0, 0.35),
    0 0 0 1px rgba(0, 0, 0, 0.18);
  cursor: ns-resize;
}

.vyby-color-picker__shade-slider::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0));
  pointer-events: none;
}

.vyby-color-picker__shade-thumb {
  position: absolute;
  left: 50%;
  top: var(--vyby-color-picker-shade-y);
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.9);
  background: var(--vyby-color-picker-shade-preview);
  box-shadow:
    0 0 0 1px rgba(0, 0, 0, 0.35),
    0 3px 10px rgba(0, 0, 0, 0.4);
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.vyby-color-picker__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.vyby-color-picker__action {
  height: 28px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.04);
  color: rgba(255, 255, 255, 0.82);
  font-size: 12px;
  padding: 0 10px;
  cursor: pointer;
}

.vyby-color-picker__action:hover {
  background: rgba(255, 255, 255, 0.08);
}

.vyby-color-picker__action.is-primary {
  border-color: rgba(243, 112, 33, 0.5);
  color: rgba(255, 255, 255, 0.94);
  background: rgba(243, 112, 33, 0.18);
}
</style>
